import { makeAutoObservable } from 'mobx';
import URI from 'urijs';

class AlcmStore {
  userStore;

  /**
   * All monitoring blocks for a property. Can include relations.
   * @type {array}
   */
  monitoringBlocks = [];

  /**
   * All MetWatch stations that the user has access to.
   * @type {array}
   */
  stations = [];

  g2Model = [];
  g3Model = {};
  riskAssessmentModel = [];

  loading = false;
  g2ModelLoading = false;
  g3ModelLoading = false;
  riskAssessmentModelLoading = false;
  stationsLoading = false;

  constructor(userStore) {
    this.userStore = userStore;
    makeAutoObservable(this);
  }

  get modelLoading() {
    return (
      this.g2ModelLoading ||
      this.g3ModelLoading ||
      this.riskAssessmentModelLoading
    );
  }

  // Fetch methods

  async fetchMonitoringBlocks(
    propertyId,
    relations = [
      'property',
      'alcmMonitoring',
      'alcmPackhouseAssessment',
      'alcmSprayData',
      'alcmTreeVigourMonitoring'
    ]
  ) {
    this.loading = true;
    this.monitoringBlocks = [];
    try {
      const response = await this._propertiesApiRequest(
        'api/alcm/monitoring-blocks',
        {
          query: {
            property_id: propertyId,
            'with[]': relations
          }
        }
      );
      const data = await response.json();
      this.monitoringBlocks = data;
      return data;
    } finally {
      this.loading = false;
    }
  }

  async fetchG2Model(blockId) {
    this.g2ModelLoading = true;
    this.g2Model = [];
    try {
      const response = await this._propertiesApiRequest(
        `api/alcm/g2-monitoring/${blockId}`
      );
      const data = await response.json();
      this.g2Model = data;
      return data;
    } finally {
      this.g2ModelLoading = false;
    }
  }

  async fetchG3Model(monitoringBlock, startDate, stopDate) {
    this.g3ModelLoading = true;
    this.g3Model = {};
    try {
      const response = await this._metwatchApiRequest(
        `api/legacy/historic/daily`,
        {
          method: 'GET',
          query: {
            station: monitoringBlock.station_id,
            start: startDate,
            stop: stopDate,
            calc_gdd: 1,
            base_temp: 3.7,
            sr_type: 'nz_nzt'
          }
        }
      );
      const data = await response.json();
      this.g3Model = data;
      return data;
    } finally {
      this.g3ModelLoading = false;
    }
  }

  async fetchRiskAssessmentModel(blockId) {
    this.riskAssessmentModelLoading = true;
    this.riskAssessmentModel = [];
    try {
      const response = await this._propertiesApiRequest(
        `api/alcm/risk-assessment/${blockId}`
      );
      const data = await response.json();
      this.riskAssessmentModel = data;
      return data;
    } finally {
      this.riskAssessmentModelLoading = false;
    }
  }

  async fetchMetWatchStations() {
    this.stationsLoading = true;
    this.stations = [];
    try {
      const response = await this._metwatchApiRequest('api/stations');
      const data = await response.json();
      this.stations = data;
      return data;
    } finally {
      this.stationsLoading = false;
    }
  }

  // Create methods

  async createMonitoringBlock(data) {
    const response = await this._propertiesApiRequest(
      'api/admin/alcm/monitoring-blocks',
      {
        method: 'POST',
        body: data
      }
    );
    const responseData = await response.json();
    this.monitoringBlocks = [...this.monitoringBlocks, responseData];
    return responseData;
  }

  async createMonitoring(data) {
    const response = await this._propertiesApiRequest(
      'api/admin/alcm/monitoring',
      {
        method: 'POST',
        body: data
      }
    );
    const responseData = await response.json();
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === data.block_id
        ? {
            ...mb,
            alcm_monitoring: [...(mb.alcm_monitoring || []), responseData]
          }
        : mb
    );
    return responseData;
  }

  async createPackhouseAssessment(data) {
    const response = await this._propertiesApiRequest(
      'api/admin/alcm/packhouse-assessment',
      { method: 'POST', body: data }
    );
    const responseData = await response.json();
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === data.block_id
        ? {
            ...mb,
            alcm_packhouse_assessment: [
              ...(mb.alcm_packhouse_assessment || []),
              responseData
            ]
          }
        : mb
    );
    return responseData;
  }

  async createSprayData(data) {
    const response = await this._propertiesApiRequest(
      'api/admin/alcm/spray-data',
      {
        method: 'POST',
        body: data
      }
    );
    const responseData = await response.json();
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === data.block_id
        ? {
            ...mb,
            alcm_spray_data: [...(mb.alcm_spray_data || []), responseData]
          }
        : mb
    );
    return responseData;
  }

  async createTreeVigourMonitoring(data) {
    const response = await this._propertiesApiRequest(
      'api/admin/alcm/tree-vigour-monitoring',
      { method: 'POST', body: data }
    );
    const responseData = await response.json();
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === data.block_id
        ? {
            ...mb,
            alcm_tree_vigour_monitoring: [
              ...(mb.alcm_tree_vigour_monitoring || []),
              responseData
            ]
          }
        : mb
    );
    return responseData;
  }

  // Update methods

  async updateMonitoringBlock(id, data) {
    const response = await this._propertiesApiRequest(
      `api/admin/alcm/monitoring-blocks/${id}`,
      { method: 'PATCH', body: data }
    );
    const responseData = await response.json();
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === id ? { ...mb, ...responseData } : mb
    );
    return responseData;
  }

  async updateMonitoring(id, data) {
    const response = await this._propertiesApiRequest(
      `api/admin/alcm/monitoring/${id}`,
      {
        method: 'PATCH',
        body: data
      }
    );
    const responseData = await response.json();
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === data.block_id
        ? {
            ...mb,
            alcm_monitoring: (mb.alcm_monitoring || []).map((m) =>
              m.id === id ? responseData : m
            )
          }
        : mb
    );
    return responseData;
  }

  async updatePackhouseAssessment(id, data) {
    const response = await this._propertiesApiRequest(
      `api/admin/alcm/packhouse-assessment/${id}`,
      { method: 'PATCH', body: data }
    );
    const responseData = await response.json();
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === data.block_id
        ? {
            ...mb,
            alcm_packhouse_assessment: (mb.alcm_packhouse_assessment || []).map(
              (ph) => (ph.id === id ? responseData : ph)
            )
          }
        : mb
    );
    return responseData;
  }

  async updateSprayData(id, data) {
    const response = await this._propertiesApiRequest(
      `api/admin/alcm/spray-data/${id}`,
      {
        method: 'PATCH',
        body: data
      }
    );
    const responseData = await response.json();
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === data.block_id
        ? {
            ...mb,
            alcm_spray_data: (mb.alcm_spray_data || []).map((sd) =>
              sd.id === id ? responseData : sd
            )
          }
        : mb
    );
    return responseData;
  }

  async updateTreeVigourMonitoring(id, data) {
    const response = await this._propertiesApiRequest(
      `api/admin/alcm/tree-vigour-monitoring/${id}`,
      { method: 'PATCH', body: data }
    );
    const responseData = await response.json();
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === data.block_id
        ? {
            ...mb,
            alcm_tree_vigour_monitoring: (
              mb.alcm_tree_vigour_monitoring || []
            ).map((tvm) => (tvm.id === id ? responseData : tvm))
          }
        : mb
    );
    return responseData;
  }

  // Delete methods

  async deleteMonitoringBlock(id) {
    await this._propertiesApiRequest(`api/admin/alcm/monitoring-blocks/${id}`, {
      method: 'DELETE'
    });
    this.monitoringBlocks = this.monitoringBlocks.filter((mb) => mb.id !== id);
  }

  async deleteMonitoring(id, blockId) {
    await this._propertiesApiRequest(`api/admin/alcm/monitoring/${id}`, {
      method: 'DELETE'
    });
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === blockId
        ? {
            ...mb,
            alcm_monitoring: (mb.alcm_monitoring || []).filter(
              (m) => m.id !== id
            )
          }
        : mb
    );
  }

  async deletePackhouseAssessment(id, blockId) {
    await this._propertiesApiRequest(
      `api/admin/alcm/packhouse-assessment/${id}`,
      {
        method: 'DELETE'
      }
    );
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === blockId
        ? {
            ...mb,
            alcm_packhouse_assessment: (
              mb.alcm_packhouse_assessment || []
            ).filter((ph) => ph.id !== id)
          }
        : mb
    );
  }

  async deleteSprayData(id, blockId) {
    await this._propertiesApiRequest(`api/admin/alcm/spray-data/${id}`, {
      method: 'DELETE'
    });
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === blockId
        ? {
            ...mb,
            alcm_spray_data: (mb.alcm_spray_data || []).filter(
              (sd) => sd.id !== id
            )
          }
        : mb
    );
  }

  async deleteTreeVigourMonitoring(id, blockId) {
    await this._propertiesApiRequest(
      `api/admin/alcm/tree-vigour-monitoring/${id}`,
      {
        method: 'DELETE'
      }
    );
    this.monitoringBlocks = this.monitoringBlocks.map((mb) =>
      mb.id === blockId
        ? {
            ...mb,
            alcm_tree_vigour_monitoring: (
              mb.alcm_tree_vigour_monitoring || []
            ).filter((tvm) => tvm.id !== id)
          }
        : mb
    );
  }

  resetG3Model() {
    this.g3Model = {};
  }

  /**
   * Helper for making Properties API requests.
   *
   * @param {string} endpoint - API endpoint path
   * @param {object} options
   * @param {string} options.method - HTTP method
   * @param {object} options.body - Request body
   * @param {object} options.query - Query parameters as key/value pairs
   * @returns {Promise<Response>} Raw fetch response
   */
  async _propertiesApiRequest(endpoint, { method = 'GET', body, query } = {}) {
    const url = new URI(
      `${process.env.REACT_APP_PROPERTIES_API_URL}${endpoint}`
    );
    if (query) {
      Object.entries(query).forEach(([key, value]) => url.addQuery(key, value));
    }

    const headers = {
      Authorization: `Bearer ${this.userStore.bearerToken}`,
      Accept: 'application/json',
      SeasonalDatabase: this.userStore.selectedDatabase
    };
    if (body) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url.valueOf(), {
      method,
      headers,
      ...(body && { body: JSON.stringify(body) })
    });
    if (!response.ok) {
      if (response.status === 401) {
        this.userStore.refresh();
      }
      throw new Error(
        `${method} ${endpoint} failed: ${response.status} ${response.statusText}`
      );
    }

    return response;
  }

  /**
   * Helper for making MetWatch API requests.
   *
   * @param {string} endpoint - API endpoint path
   * @param {object} options
   * @param {string} options.method - HTTP method
   * @param {object} options.body - Request body
   * @param {object} options.query - Query parameters as key/value pairs
   * @returns {Promise<Response>} Raw fetch response
   */
  async _metwatchApiRequest(endpoint, { method = 'GET', body, query } = {}) {
    const url = new URI(`${process.env.REACT_APP_METWATCH_API_URL}${endpoint}`);
    if (query) {
      Object.entries(query).forEach(([key, value]) => url.addQuery(key, value));
    }

    const headers = {
      Authorization: `Bearer ${this.userStore.bearerToken}`,
      Accept: 'application/json'
    };
    if (body) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url.valueOf(), {
      method,
      headers,
      ...(body && { body: JSON.stringify(body) })
    });
    if (!response.ok) {
      if (response.status === 401) {
        this.userStore.refresh();
      }
      throw new Error(
        `${method} ${endpoint} failed: ${response.status} ${response.statusText}`
      );
    }

    return response;
  }
}

export default AlcmStore;

